import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { getAccountActivation } from '../../lib/flotiq-client';
import { parseResponseErrors } from '../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../lib/helpers';
import { detectLanguage } from '../../lib/language';

import Loader from '../../components/Loader/Loader';

import useDarkMode from '../../hooks/useDarkMode';

const testId = 'account-activation';

const AccountActivation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenParam = searchParams.get('activationToken');
  const resetPasswordToken = searchParams.get('resetPasswordToken');
  const requestRef = useRef<boolean>(null);
  const [darkMode] = useDarkMode();
  const [, , { removeItem: removeUser }] = useLocalStorageState('cms.user');
  const { lang } = useParams();
  const targetLang = lang ?? detectLanguage();

  const fetchActivation = useCallback(
    async (tokenParam: string) => {
      if (requestRef.current) return;
      requestRef.current = true;
      try {
        if (resetPasswordToken) {
          navigate(
            `/${targetLang}/change-password?resetPasswordToken=${resetPasswordToken}&` +
              `activationToken=${tokenParam}&mode=setPassword`,
          );
          return;
        }
        const { body, status, ok } = await getAccountActivation(tokenParam);
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        navigate(`/${targetLang}/login?activated=true`);
      } catch {
        navigate(`/${targetLang}/login?activated=false`);
      }
    },
    [navigate, resetPasswordToken, targetLang],
  );

  useEffect(() => {
    removeUser();
  }, [removeUser]);

  useEffect(() => {
    if (tokenParam) {
      fetchActivation(tokenParam);
    }
  }, [tokenParam, fetchActivation, resetPasswordToken, navigate]);

  return (
    <>
      <title>{t('Global.RegisterToFlotiq')}</title>
      <div
        className={twMerge(
          'w-full h-screen flex items-center justify-center',
          darkMode && 'dark:bg-slate-900',
        )}
      >
        <Loader
          size="big"
          type="spinner-grid"
          {...getTestProps(testId, 'loader', 'testId')}
        />
      </div>
    </>
  );
};

export default AccountActivation;
